import { provideHttpClient, withInterceptors } from '@angular/common/http';
import type { ApplicationConfig } from '@angular/core';
import { ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';

import { routes } from './app.routes';
import { provideErrorNotifier, providePortalAvailability } from './core/error/error.providers';
import { GlobalErrorHandler } from './core/error/global-error.handler';
import { credentialsInterceptor } from './core/interceptors/credentials.interceptor';
import { httpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { technicalLogInterceptor } from './core/interceptors/technical-log.interceptor';
import { registerSupportedLocaleData } from './core/locale/supported-locale-data';
import { provideRealtime } from './core/realtime';
import { provideTechnicalLog } from './core/telemetry';
import { provideTenantConfiguration } from './core/tenant';
import type { Environment } from './core/tokens/app.tokens';
import { ENVIRONMENT } from './core/tokens/app.tokens';
import { provideAuth } from './features/auth';
import { provideVoiceReading } from './features/chat';
import { provideMicrophoneAccess } from './features/voice-message';
import { provideNotifications } from './features/notifications';
import { provideReports } from './features/reports';
import {
  provideActiveConversation,
  provideLegalDocuments,
  providePresetFlow,
} from './features/onboarding';
import { provideOperatorPushRouting, provideSettings } from './features/settings';
import { provideLocalizationBootstrap, provideShell, provideTheme } from './features/shell';

// Prima dei provider: una data si formatta con i dati della propria lingua, e il primo `DatePipe` puo'
// girare gia' al primo render della shell.
registerSupportedLocaleData();

/**
 * I provider dell'applicazione, con l'ambiente che le tocca in sorte.
 *
 * @remarks
 * L'ambiente arriva da fuori invece di essere importato qui perche' l'indirizzo del BFF lo dichiara il
 * deployment e si legge all'avvio (`core/configuration/runtime-environment.ts`): chi avvia l'applicazione
 * lo risolve una volta e lo consegna, e resta un solo punto da cui `ENVIRONMENT` prende il proprio valore.
 */
export const appConfigFor = (environment: Environment): ApplicationConfig => ({
  providers: [
    // Un solo giro di change detection per piu' eventi arrivati nello stesso frame. Senza, ogni messaggio
    // dell'hub — ogni pezzo di una traduzione in streaming, ogni segmento della dettatura — ricontrollava
    // l'applicazione intera per conto suo.
    provideZoneChangeDetection({ eventCoalescing: true }),
    // 'computed' perche' l'indietro del browser si ferma quasi ovunque (`browserBackGuard`): col default
    // il Router, fermandolo, riscrive l'indirizzo sulla voce in cui il browser e' gia' arrivato, e la
    // cronologia perde un passo a ogni tentativo. Cosi' invece ci torna sopra.
    provideRouter(routes, withRouterConfig({ canceledNavigationResolution: 'computed' })),
    // Il diario tecnico e' l'ultimo della fila: cosi' registra l'esito che gli altri hanno gia'
    // tradotto — un 401 su rotta anonima che diventa un redirect si legge nel diario come il 401 che
    // era — e non vede mai una richiesta che l'interceptor delle credenziali non ha ancora firmato.
    provideHttpClient(
      withInterceptors([credentialsInterceptor, httpErrorInterceptor, technicalLogInterceptor]),
    ),
    { provide: ENVIRONMENT, useValue: environment },
    // Radice e non rotta lazy: decide cosa mostra l'intestazione, che esiste prima di ogni rotta.
    // Prima di tutto il resto: quel che succede all'avvio — la configurazione che non arriva, il
    // biglietto rifiutato — e' esattamente cio' che una segnalazione deve poter spiegare.
    provideTechnicalLog(),
    provideTenantConfiguration(),
    // In radice, non su una rotta lazy: e' cio' che rende la connessione a DxpHub una sola.
    provideRealtime(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideErrorNotifier(),
    providePortalAvailability(),
    provideAuth(),
    // In radice: lo scrive la chat e lo legge la guardia dell'ingresso, e nessuna delle due monta
    // l'onboarding.
    provideActiveConversation(),
    providePresetFlow(),
    // In radice: l'informativa la apre la chat e la apre l'avviso sui cookie, che sta nelle due cornici
    // e c'e' prima di ogni rotta.
    provideLegalDocuments(),
    provideShell(),
    provideLocalizationBootstrap(),
    provideTheme(),
    provideNotifications(),
    // In radice, non su una rotta lazy: il comando che apre la segnalazione sta nell'intestazione,
    // che esiste prima di ogni rotta.
    provideReports(),
    provideOperatorPushRouting(),
    provideSettings(),
    // In radice come le impostazioni del cittadino, e per la stessa ragione: la voce serve alla chat
    // per leggere i messaggi e alla pagina delle impostazioni per farla sentire prima di sceglierla.
    provideVoiceReading(),
    // Il permesso del microfono, per la stessa ragione: lo chiedono la chat e le impostazioni.
    provideMicrophoneAccess(),
  ],
});
